import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employeeApi, aiApi } from '../../services/api';
import { BandBadge, Spinner, PageHeader } from '../../components/shared/index';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { SparklesIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import EmployeeSkillsTab from './EmployeeSkillsTab';
import { useAuth } from '../../context/AuthContext';

const AI_RISK_STYLES = {
  high:   'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low:    'bg-green-100 text-green-800 border-green-200',
};
const AI_READY_STYLES = {
  ready:      'bg-green-100 text-green-800 border-green-200',
  developing: 'bg-blue-100 text-blue-800 border-blue-200',
  not_ready:  'bg-gray-100 text-gray-700 border-gray-200',
};

function AiLevelChip({ value, map }) {
  const cls = map[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize', cls)}>
      {String(value || 'unknown').replace('_', ' ')}
    </span>
  );
}

function AiSignalPill({ signal }) {
  const text = typeof signal === 'string' ? signal : (signal?.label || signal?.type || signal?.name || '—');
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-50 text-gray-700 border border-gray-200">{text}</span>;
}

function AiInsightCard({ title, score, level, signals, levelMap, accentClass }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className={clsx('w-4 h-4', accentClass)} />
          <p className="text-sm font-semibold text-gray-700">{title}</p>
        </div>
        <AiLevelChip value={level} map={levelMap} />
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <p className="text-4xl font-bold text-gray-900">{score != null ? Number(score).toFixed(0) : '—'}</p>
        <span className="text-xs text-gray-400">/ 100</span>
      </div>
      {signals?.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {signals.slice(0, 3).map((s, i) => <AiSignalPill key={i} signal={s} />)}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No signals available</p>
      )}
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const { isManager } = useAuth();
  const { data: emp, isLoading } = useQuery({ queryKey: ['employee', id], queryFn: () => employeeApi.get(id).then(r => r.data) });
  const { data: history } = useQuery({ queryKey: ['employee-history', id], queryFn: () => employeeApi.history(id).then(r => r.data) });
  const { data: aiAttrition } = useQuery({
    queryKey: ['ai-attrition-emp', id],
    queryFn:  () => aiApi.attritionRisk(id).then(r => r.data),
    enabled:  !!isManager && !!id,
    retry:    false,
  });
  const { data: aiPromotion } = useQuery({
    queryKey: ['ai-promotion-emp', id],
    queryFn:  () => aiApi.promotionReadiness(id).then(r => r.data),
    enabled:  !!isManager && !!id,
    retry:    false,
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!emp)      return <p className="p-6 text-red-600">Employee not found</p>;

  const scoreData = history?.scores?.map(s => ({ cycle: s.cycle?.name?.slice(-6), score: s.final_score })) || [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/employees" className="text-sm text-blue-600 hover:underline mb-4 inline-block">← Back to employees</Link>
      <div className="card p-6 mb-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-2xl font-bold shrink-0">
            {emp.name[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{emp.name}</h1>
            <p className="text-gray-500">{emp.job_title || emp.role?.replace('_',' ')} · {emp.department?.name || 'No department'}</p>
            <p className="text-sm text-gray-400 mt-1">{emp.email}</p>
            <div className="flex gap-3 mt-3 flex-wrap">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 capitalize">{emp.band}</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 capitalize">{emp.status}</span>
              {emp.hire_date && <span className="text-xs text-gray-400">Hired: {emp.hire_date}</span>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Manager</p>
            <p className="font-medium">{emp.manager?.name || '—'}</p>
          </div>
        </div>
      </div>

      {isManager && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <AiInsightCard
            title="Attrition risk"
            score={aiAttrition?.score}
            level={aiAttrition?.level}
            signals={aiAttrition?.signals}
            levelMap={AI_RISK_STYLES}
            accentClass="text-red-500"
          />
          <AiInsightCard
            title="Promotion readiness"
            score={aiPromotion?.score}
            level={aiPromotion?.level}
            signals={aiPromotion?.signals}
            levelMap={AI_READY_STYLES}
            accentClass="text-blue-500"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score trend */}
        <div className="card p-5">
          <h2 className="section-title">Score history</h2>
          {scoreData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={scoreData}>
                <XAxis dataKey="cycle" tick={{ fontSize: 11 }} />
                <YAxis domain={[0,100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => [`${v}`, 'Final score']} />
                <Line type="monotone" dataKey="score" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-sm">No scored cycles yet</p>}
        </div>

        {/* Rating history */}
        <div className="card p-5">
          <h2 className="section-title">Rating history</h2>
          <div className="space-y-2">
            {history?.ratings?.length > 0 ? history.ratings.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-sm text-gray-700">{r.cycle?.name}</span>
                <BandBadge band={r.band} label={r.band_label} />
              </div>
            )) : <p className="text-gray-400 text-sm">No ratings yet</p>}
          </div>
        </div>

        {/* Direct reports */}
        {emp.directReports?.length > 0 && (
          <div className="card p-5">
            <h2 className="section-title">Direct reports ({emp.directReports.length})</h2>
            <div className="space-y-2">
              {emp.directReports.map(r => (
                <Link key={r.id} to={`/employees/${r.id}`} className="flex items-center gap-3 py-2 hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors">
                  <div className="w-7 h-7 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-semibold">{r.name[0]}</div>
                  <div><p className="text-sm font-medium">{r.name}</p><p className="text-xs text-gray-400 capitalize">{r.role?.replace('_',' ')}</p></div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active PIP */}
        {history?.pips?.filter(p => p.status === 'active').map(pip => (
          <div key={pip.id} className="card p-5 border-red-200 bg-red-50">
            <h2 className="section-title text-red-700">Active PIP</h2>
            <p className="text-sm text-red-600 mb-2">Started: {pip.start_date} · Ends: {pip.end_date}</p>
            <Link to={`/pips/${pip.id}`} className="text-sm text-red-700 underline">View PIP details →</Link>
          </div>
        ))}
      </div>

      {/* Skills & competencies */}
      <div className="mt-6">
        <EmployeeSkillsTab employeeId={emp.id} />
      </div>
    </div>
  );
}
